import * as React from "react";
import { faExternalLink } from "@fortawesome/pro-solid-svg-icons/faExternalLink";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons/faQuestionCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DotDotDots } from "~/components/DotDotDots";
import { Tooltip } from "~/components/Tooltip";
import { formatApy } from "~/utils/functions/formatApy";
import { formatCurrencyValue } from "~/utils/functions/formatCurrencyValue";
import {
    AggregateValueType,
    AggregateValueTypes,
    PercentChangeProps,
} from "./types";
import { cn } from "~/lib/utils";
import { Link } from "../Link";
import { Anchor } from "../Anchor";
import { Button } from "../ui/button";
import { ArrowRightIcon } from "lucide-react";

// // // //

function formatCurrencyValueCompact(value: number): string {
    // Handle trillion or greater
    if (value >= 1_000_000_000_000) {
        return `${(value / 1_000_000_000_000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}T`;
    }

    // Handle billion or greater
    if (value >= 1_000_000_000) {
        return `${(value / 1_000_000_000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}B`;
    }
    // Handle million or greater but less than a billion
    else if (value >= 1_000_000) {
        return `${(value / 1_000_000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}M`;
    }
    // Handle 10k or greater but less than a million
    else if (value >= 10_000) {
        return `${(value / 1000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}K`;
    }

    return value.toLocaleString("en-US", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    });
}

// // // //

function formatCountValueCompact(value: number): string {
    // Handle trillion or greater
    if (value >= 1_000_000_000_000) {
        return `${(value / 1_000_000_000_000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}T`;
    }

    // Handle billion or greater
    if (value >= 1_000_000_000) {
        return `${(value / 1_000_000_000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}B`;
    }
    // Handle million or greater but less than a billion
    else if (value >= 1_000_000) {
        return `${(value / 1_000_000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}M`;
    }
    // Handle 10k or greater but less than a million
    else if (value >= 10_000) {
        return `${(value / 1000).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}K`;
    }

    return value.toLocaleString("en-US", {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    });
}

// // // //

/**
 * Formats numeric values for AggregateDisplay component
 */
function formatAggregateValue(props: {
    value: number | string;
    type: AggregateValueType;
}): string {
    const { value, type } = props;

    // Handle "string" value (just return it)
    if (typeof value === "string") return value;

    // Handle "dollar"
    if (type === AggregateValueTypes.dollar)
        return `$${formatCurrencyValue(value)}`;

    // Handle "dollarShortened"
    if (type === AggregateValueTypes.dollarCompact)
        return `$${formatCurrencyValueCompact(value)}`;

    // Handle "percent"
    if (type === AggregateValueTypes.percent)
        return `${formatApy(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}%`;

    // Handle "years"
    if (type === AggregateValueTypes.year)
        return `${value.toLocaleString()} yrs`;

    // Handle "bps"
    if (type === AggregateValueTypes.bps)
        return `${value.toLocaleString()} bps`;

    // Handle "countCompact"
    if (type === AggregateValueTypes.countCompact) {
        return formatCountValueCompact(value);
    }

    // Handle "tokens" + "count" + "fallback"
    return value.toLocaleString();
}

// // // //

function PercentChangeDisplay(
    props: PercentChangeProps & { className?: string },
) {
    const { value, interval, className } = props;

    const isPositive = value >= 0;
    const arrowIcon = isPositive ? "▲" : "▼";
    const colorClass = isPositive ? "text-change-up" : "text-change-down";

    return (
        <div className={cn("flex items-center space-x-1 text-sm", className)}>
            <span className={`font-semibold ${colorClass}`}>{arrowIcon}</span>
            <span className={colorClass}>
                {isPositive ? "+" : null}
                {Math.abs(formatApy(value)).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })}
                %
            </span>
            <span className="text-muted-foreground font-light tracking-wide">
                from {interval} ago
            </span>
        </div>
    );
}

// // // //

export interface Props {
    label: string;
    tooltip?: string;
    value: number | string;
    loading?: boolean;
    type: AggregateValueType;
    percentChange?: PercentChangeProps;
    card?: boolean;
    linkTo?: {
        label: string;
        href: string;
        analyticsLabel: string;
    };
    tooltipLinkTo?: {
        label: string;
        href: string;
        analyticsLabel: string;
    };
}

/**
 * Displays an aggregate/summary level value in the dashboard
 */
export function AggregateDisplay(
    props: Props & {
        useCompactMobileMetricGrid?: boolean;
    },
) {
    const {
        label,
        tooltip,
        value,
        loading = false,
        type,
        percentChange = null,
        card = false,
        linkTo,
        tooltipLinkTo,
        useCompactMobileMetricGrid = false,
    } = props;
    const formattedValue = formatAggregateValue({ value, type });
    const useSmallerFittedValue =
        useCompactMobileMetricGrid && formattedValue.length >= 14;

    const showPercentChange =
        percentChange !== null && value !== 0 && percentChange.value !== 0;

    return (
        <div
            className={cn(
                "col-span-1 flex flex-col md:justify-start lg:items-start",
                {
                    "bg-card p-4 rounded-lg border": card,
                },
            )}
        >
            <div className="flex items-center gap-1.5 leading-snug">
                <p className="text-sidebar-foreground font-sans font-light text-base leading-snug">
                    {label}
                </p>

                {tooltip !== undefined && (
                    <div className="inline-block">
                        <Tooltip
                            interactive={!!tooltipLinkTo}
                            delayHide={tooltipLinkTo ? 150 : 0}
                            placement="bottom-start"
                            tooltipContent={
                                <div className="py-2 px-3 max-w-[20rem]">
                                    <p className="text-sm font-light">
                                        {tooltip}
                                    </p>
                                    {tooltipLinkTo && (
                                        <Anchor
                                            href={tooltipLinkTo.href}
                                            openInNewTab
                                            analyticsLabel={
                                                tooltipLinkTo.analyticsLabel
                                            }
                                            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-300 hover:text-blue-200 transition-colors mt-2"
                                        >
                                            {tooltipLinkTo.label}
                                            <FontAwesomeIcon
                                                icon={faExternalLink}
                                                className="h-3"
                                            />
                                        </Anchor>
                                    )}
                                </div>
                            }
                        >
                            {({ setTriggerRef }) => (
                                <span ref={setTriggerRef}>
                                    <FontAwesomeIcon
                                        icon={faQuestionCircle}
                                        className="text-muted-foreground hover:text-muted-foreground-hover transition-colors h-4 cursor-help -ml-0.5"
                                    />
                                </span>
                            )}
                        </Tooltip>
                    </div>
                )}
            </div>

            <p
                className={cn(
                    "text-foreground font-sans font-light text-[1.938rem]!",
                    {
                        "max-sm:tracking-tight": useCompactMobileMetricGrid,
                        "max-sm:text-[1.625rem]!":
                            useCompactMobileMetricGrid &&
                            !useSmallerFittedValue,
                        "max-sm:text-[1.4375rem]!": useSmallerFittedValue,
                    },
                )}
            >
                {!loading && formattedValue}
                {loading && <DotDotDots renderPlaceholder />}
            </p>

            {/* Render percent change display, when available */}
            {showPercentChange && (
                <PercentChangeDisplay
                    {...percentChange}
                    className={cn({
                        "max-sm:flex-wrap max-sm:gap-x-1 max-sm:space-x-0 max-sm:[&>span:last-child]:basis-full max-sm:[&>span:last-child]:whitespace-nowrap max-sm:[&>span:last-child]:text-xs":
                            useCompactMobileMetricGrid,
                    })}
                />
            )}

            {linkTo && (
                <Button
                    asChild
                    variant="link"
                    size="lg"
                    className="-mb-1 p-0 self-start"
                >
                    <Link
                        href={linkTo.href}
                        analyticsLabel={linkTo.analyticsLabel}
                    >
                        {linkTo.label}
                        <ArrowRightIcon />
                    </Link>
                </Button>
            )}
        </div>
    );
}

// // // //

export function AggregateGrid(props: {
    aggregates: Array<Props>;
    useCompactMobileMetricGrid?: boolean;
}) {
    const { aggregates, useCompactMobileMetricGrid = false } = props;

    return (
        <div
            // ENHANCEMENT - refine this grid layout logic
            className={cn(
                "col-span-1 lg:col-span-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 my-4",
                {
                    "xl:grid-cols-3": aggregates.length === 3,
                    "xl:grid-cols-4":
                        aggregates.length === 4 ||
                        aggregates.length === 7 ||
                        aggregates.length === 8,
                    "xl:grid-cols-5":
                        aggregates.length === 5 || aggregates.length === 10,
                    "xl:grid-cols-6": aggregates.length === 6,
                },
                {
                    "max-sm:grid-cols-2 max-sm:gap-x-6 max-sm:[&>div]:min-w-0 max-sm:[&>div>div:first-child>p]:text-[0.9375rem]":
                        useCompactMobileMetricGrid,
                },
            )}
        >
            {aggregates.map((aggregate) => (
                <AggregateDisplay
                    key={aggregate.label}
                    {...aggregate}
                    useCompactMobileMetricGrid={useCompactMobileMetricGrid}
                />
            ))}
        </div>
    );
}
